import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';

interface AdminRouteProps {
  component: React.ComponentType<any>;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ component: Component }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [checkingRole, setCheckingRole] = useState(true);

  useEffect(() => {
    const getUserRole = async () => {
      if (isAuthenticated && !isLoading) {
        try {
          const token = await getAccessTokenSilently();
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const role = payload['https://app.fxer.ai/role'];
            setUserRole(role || null);
          }
        } catch (error) {
          console.error('Error getting user role:', error);
          setUserRole(null);
        } finally {
          setCheckingRole(false);
        }
      } else if (!isLoading) {
        setCheckingRole(false);
      }
    };

    getUserRole();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  // Still loading auth or checking role
  if (isLoading || checkingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Not an admin - redirect to home
  if (userRole !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  // User is authenticated and is an admin
  return <Component />;
};

export default AdminRoute;
